import { LuX } from '@react-icons/all-files/lu/LuX'
import {
  Box,
  Button,
  Dialog,
  Flex,
  IconButton,
  ScrollArea,
  Text,
} from '@radix-ui/themes'
import React, { useState } from 'react'

import { useTranslation } from '@disco/i18n/use-translation'

import { SearchTextField } from '../search-text-field'
import styles from './index.module.css'
import { SearchResults } from './search-results'
import { type SearchFn, useDebouncedSearch } from './use-debounced-search'

type Props<T> = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  /** Fetches results for a query; leave out anything that shouldn't be selectable */
  search: SearchFn<T>
  getKey: (item: T) => string | number
  /** Plain-text name, used for accessible labels */
  getItemLabel: (item: T) => string
  /** Renders a search result next to its checkbox */
  renderItem: (item: T) => React.ReactNode
  /** Renders an item in the selected list; defaults to its label */
  renderSelectedItem?: (item: T) => React.ReactNode
  /** Called with the selected items in the order they were picked; the dialog closes once it resolves, and stays open if it rejects */
  onConfirm: (selected: T[]) => Promise<void> | void
  confirmLabel: (count: number) => React.ReactNode
  /** Maximum number of items that can be selected */
  max?: number
  placeholder?: string
  /** Shown in place of results before anything is typed */
  emptyQueryMessage?: React.ReactNode
}

/**
 * Dialog for searching and selecting several items before confirming.
 * Selected items are kept in their own list above the search, so they survive
 * changes to the query.
 * @param open - Whether the dialog is open
 * @param onOpenChange - Called when the dialog opens or closes
 * @param title - Dialog title
 * @param search - Fetches results for a query, debounced, with stale requests aborted
 * @param getKey - Unique key for an item
 * @param getItemLabel - Plain-text name of an item, used for accessible labels
 * @param renderItem - Renders a search result
 * @param renderSelectedItem - Renders a selected item; defaults to its label
 * @param onConfirm - Called with the selected items; the dialog can't be closed until it settles, then closes if it resolves
 * @param confirmLabel - Label of the confirm button, given the number selected
 * @param max - Maximum number of items that can be selected
 * @param placeholder - Search field placeholder
 * @param emptyQueryMessage - Shown before anything is typed
 */
export const SearchSelectDialog = <T,>({
  open,
  onOpenChange,
  title,
  search,
  getKey,
  getItemLabel,
  renderItem,
  renderSelectedItem,
  onConfirm,
  confirmLabel,
  max = Infinity,
  placeholder,
  emptyQueryMessage,
}: Props<T>): React.ReactNode => {
  const { t } = useTranslation('common')
  const [selected, setSelected] = useState<T[]>([])
  const [isConfirming, setIsConfirming] = useState(false)
  const { query, setQuery, results, isSearching, hasError, retry } =
    useDebouncedSearch(search)
  const selectedKeys = selected.map(getKey)
  const isFull = selected.length >= max

  const toggle = (item: T) => {
    const key = getKey(item)

    if (selectedKeys.includes(key)) {
      setSelected(selected.filter(selectedItem => getKey(selectedItem) !== key))
    } else if (!isFull) {
      setSelected([...selected, item])
    }
  }

  const close = () => {
    setSelected([])
    setQuery('')
    onOpenChange(false)
  }

  const handleOpenChange = (open: boolean) => {
    if (open) {
      onOpenChange(true)
    } else if (!isConfirming) {
      close()
    }
  }

  const confirm = async () => {
    setIsConfirming(true)
    try {
      await onConfirm(selected)
      close()
    } catch {
      // Callers report their own errors; staying open keeps the selection for a retry
    } finally {
      setIsConfirming(false)
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content maxWidth="640px" aria-describedby={undefined}>
        <Dialog.Title>{title}</Dialog.Title>

        {selected.length > 0 && (
          <Box mb="3" data-testid="search-select-dialog-selected">
            <Text as="p" size="2" weight="medium" mb="2">
              {Number.isFinite(max)
                ? t('common:selectedCount', 'Selected ({{current}}/{{max}})', {
                    current: selected.length,
                    max,
                  })
                : t('common:selectedTotal', 'Selected ({{current}})', {
                    current: selected.length,
                  })}
            </Text>
            <Flex direction="column" gap="1">
              {selected.map(item => (
                <Flex
                  key={getKey(item)}
                  align="center"
                  gap="2"
                  className={styles.selectedRow}
                >
                  <Box flexGrow="1" minWidth="0">
                    {renderSelectedItem ? (
                      renderSelectedItem(item)
                    ) : (
                      <Text as="p" size="2" truncate>
                        {getItemLabel(item)}
                      </Text>
                    )}
                  </Box>
                  <IconButton
                    variant="ghost"
                    color="gray"
                    size="1"
                    aria-label={t('common:removeItemName', 'Remove {{name}}', {
                      name: getItemLabel(item),
                    })}
                    onClick={() => toggle(item)}
                  >
                    <LuX />
                  </IconButton>
                </Flex>
              ))}
            </Flex>
          </Box>
        )}

        <SearchTextField
          value={query}
          placeholder={placeholder}
          onChange={event => setQuery(event.target.value)}
        />

        <ScrollArea
          type="auto"
          scrollbars="vertical"
          className={styles.results}
        >
          <Box px="1">
            <SearchResults
              query={query}
              results={results}
              isSearching={isSearching}
              hasError={hasError}
              onRetry={retry}
              selectedKeys={selectedKeys}
              isFull={isFull}
              getKey={getKey}
              getItemLabel={getItemLabel}
              renderItem={renderItem}
              onToggle={toggle}
              emptyQueryMessage={emptyQueryMessage}
            />
          </Box>
        </ScrollArea>

        <Flex gap="3" mt="4" justify="end">
          <Button
            variant="soft"
            color="gray"
            disabled={isConfirming}
            onClick={close}
          >
            {t('common:cancel', 'Cancel')}
          </Button>
          <Button
            disabled={selected.length === 0 || isConfirming}
            loading={isConfirming}
            onClick={confirm}
          >
            {confirmLabel(selected.length)}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
